import { GeneratedImage } from 'components/BackgroundReplacer/Gallery/types';
import { BackgroundGenerationOptions } from 'components/BackgroundReplacer/types';
import generatorsApi, {
  GeneratedContentStatus,
  ReplaceBackgroundResponse,
} from 'global/generatorsApi';
import { GeneratedContent } from 'global/types/Generators';
import { RemoveBackgroundError } from 'stores/backgroundReplacerStore/types';

import {
  buildImageProxyUrlByObjectName,
  uploadImage,
  removeBackground as removeBackgroundApi,
} from '../../components/AiTools/utils';

export type GeneratedContentResult =
  | { error: string }
  | {
      items: GeneratedImage[];
      pages: { current: number; total: number } | null;
    };

export const defaultImageSize = 1024;

export const selectGeneratedContent = async (
  page: number,
  itemsPerPage: number,
  labels: Record<string, string>,
  initialImage?: string
): Promise<GeneratedContentResult> => {
  try {
    const generatedContent = await generatorsApi.selectGeneratedContent(
      itemsPerPage,
      page * itemsPerPage,
      GeneratedContentStatus.Failed
    );
    if (!generatedContent.success) {
      return {
        error: generatedContent.error,
      };
    }

    const images = convertGeneratedImages(
      generatedContent.results,
      labels,
      initialImage
    );
    if (images.length === 0 && generatedContent.total > 0) {
      return await selectGeneratedContent(
        0,
        itemsPerPage,
        labels,
        initialImage
      );
    }

    let pages = null;
    if (generatedContent.total > itemsPerPage) {
      pages = {
        current: page,
        total: Math.ceil(generatedContent.total / itemsPerPage),
      };
    }

    return {
      items: images,
      pages: pages,
    };
  } catch (e) {
    return {
      error: e instanceof Error ? e.message : 'unknown error',
    };
  }
};

export interface GenerateImageParams {
  id: string;
  status: GeneratedContentStatus;
  styleId?: string;
  objectName?: string;
  loadingImage?: string;
  createdAt?: string;
  prompt?: string;
  negativePrompt?: string;
  sceneId?: number;
}

export const createGeneratedImage = (
  generateImageParams: GenerateImageParams,
  labels: Record<string, string>
): GeneratedImage | null => {
  if (generateImageParams.status === 'FAILED') {
    return null;
  }

  const sceneName = generateImageParams.styleId
    ? labels[generateImageParams.styleId]
    : undefined;

  const isLoading = generateImageParams.status !== 'COMPLETE';
  return {
    id: generateImageParams.id,
    isLoading: isLoading,
    label: sceneName,
    src:
      !isLoading && generateImageParams.objectName
        ? buildImageProxyUrlByObjectName(
            generateImageParams.objectName,
            defaultImageSize,
            defaultImageSize
          )
        : generateImageParams.loadingImage,
    createdAt: generateImageParams.createdAt,
    prompt: generateImageParams.prompt,
    negativePrompt: generateImageParams.negativePrompt,
    sceneId: generateImageParams.sceneId,
  };
};

export const convertGeneratedImages = (
  images: GeneratedContent[],
  labels: Record<string, string>,
  loadingImage?: string
): GeneratedImage[] => {
  return images
    .map((x) =>
      createGeneratedImage(
        {
          id: x.id,
          status: x.status,
          styleId: x.styleId,
          objectName: x.objectName,
          loadingImage: loadingImage,
          createdAt: x.createdAt,
          prompt: x.prompt,
          negativePrompt: x.negativePrompt,
          sceneId: x.sceneId,
        },
        labels
      )
    )
    .filter((x) => x !== null) as GeneratedImage[];
};

export const removeBackground = async (
  file: File,
  dimensions: { width: number; height: number }
): Promise<{ url: string | null; error?: RemoveBackgroundError }> => {
  const { url: uploadUrl, error } = await uploadImage(
    file,
    dimensions.width,
    dimensions.height,
    'png',
    true
  );
  if (error || !uploadUrl) {
    return { url: null, error: RemoveBackgroundError.UNKNOWN_UPLOAD_ERROR };
  }

  return await removeBackgroundApi(
    uploadUrl,
    dimensions.width,
    dimensions.height
  );
};

export const generateImage = async (
  sourceImage: string,
  options: BackgroundGenerationOptions
): Promise<ReplaceBackgroundResponse> => {
  return await generatorsApi.replaceBackground(
    sourceImage,
    'presetId' in options ? options.presetId : undefined,
    'includePrompt' in options ? options.includePrompt : undefined,
    'excludePrompt' in options && options.excludePrompt
      ? options.excludePrompt
      : undefined
  );
};
